import { useCallback, useEffect, useRef } from 'react';
import { useTimesheetStore } from '../stores/timesheetStore';
import { useUsersStore } from '../stores/usersStore';
import { getGroupingConfig } from '../config/timesheet-grouping';
import { getTimesheetTimeline } from '../api/timesheet.api';
import { fetchWorkloadData } from '../services/workload-data.service';
import type { ProgressRow, ProgressGroup } from '../types/timesheet.types';

// ─── Which data each progress view needs ─────────────────────────────────────

const NEEDS_PLANNED = new Set([
  'progress_utilization',
  'progress_planned_vs_actual',
]);

const NEEDS_LOGGED = new Set([
  'progress_timesheet',
  'progress_planned_vs_actual',
]);

// ─── Helpers ─────────────────────────────────────────────────────────────────

function sumField(
  rows: ProgressRow[],
  field: 'requiredHours' | 'loggedHours' | 'plannedHours',
): number {
  return Math.round(rows.reduce((sum, r) => sum + r[field], 0) * 100) / 100;
}

function sumBillableHours(rows: ProgressRow[]): number | null {
  if (rows.every((r) => r.billableHours == null)) return null;
  return Math.round(rows.reduce((sum, r) => sum + (r.billableHours ?? 0), 0) * 100) / 100;
}

function groupProgressRows(
  rows: ProgressRow[],
  selectedTeamIds?: number[],
): ProgressGroup[] {
  if (!selectedTeamIds || selectedTeamIds.length === 0) {
    return [
      {
        groupId: 'all',
        groupName: 'All Users',
        rows,
        totalRequiredHours: sumField(rows, 'requiredHours'),
        totalLoggedHours: sumField(rows, 'loggedHours'),
        totalPlannedHours: sumField(rows, 'plannedHours'),
        totalBillableHours: sumBillableHours(rows),
      },
    ];
  }

  // Group by team
  const groupMap = new Map<string, ProgressRow[]>();
  for (const row of rows) {
    const key =
      row.teamId && row.teamName
        ? `${row.teamId}:${row.teamName}`
        : 'unassigned:Unassigned';
    if (!groupMap.has(key)) groupMap.set(key, []);
    groupMap.get(key)!.push(row);
  }

  return Array.from(groupMap.entries()).map(([key, groupRows]) => {
    const [groupId, ...nameParts] = key.split(':');
    const groupName = nameParts.join(':');
    return {
      groupId,
      groupName,
      rows: groupRows,
      totalRequiredHours: sumField(groupRows, 'requiredHours'),
      totalLoggedHours: sumField(groupRows, 'loggedHours'),
      totalPlannedHours: sumField(groupRows, 'plannedHours'),
      totalBillableHours: sumBillableHours(groupRows),
    };
  });
}

// ─── Hook ────────────────────────────────────────────────────────────────────

/**
 * Orchestrates data fetching for the 3 progress views.
 * Only runs when the current groupBy is a progress view.
 *
 * Uses the centralised workload-data service for planned hours + capacity,
 * which correctly passes user schedules and leaves to extractScheduledWork().
 */
export function useProgressData(): void {
  const {
    groupBy,
    dateRange,
    filters,
    setProgressGroups,
    setProgressLoading,
    setProgressError,
  } = useTimesheetStore();

  const users = useUsersStore((s) => s.users);
  const abortRef = useRef<AbortController | null>(null);

  const fetchData = useCallback(async () => {
    // Always abort any previous in-flight request first (e.g. when switching views)
    abortRef.current?.abort();

    // Only run for progress views
    const config = getGroupingConfig(groupBy);
    if (config.viewMode !== 'progress') return;

    if (!dateRange.start || !dateRange.end) return;

    if (filters.userIds.length === 0) {
      setProgressGroups([]);
      setProgressLoading(false);
      return;
    }

    // Create new abort controller for this request
    const controller = new AbortController();
    abortRef.current = controller;

    setProgressLoading(true);
    setProgressError(null);

    try {
      const userIds = filters.userIds;
      const needsPlanned = NEEDS_PLANNED.has(groupBy);
      const needsLogged = NEEDS_LOGGED.has(groupBy);

      // Parallel fetch: workload data (capacity + planned) + worklogs (conditional)
      const workloadPromise = fetchWorkloadData({
        startDate: dateRange.start,
        endDate: dateRange.end,
        userIds,
        skipItems: !needsPlanned,
        boardIds:
          filters.boardIds.length > 0 ? filters.boardIds : undefined,
        signal: controller.signal,
      });

      const worklogsPromise = needsLogged
        ? getTimesheetTimeline(
            {
              startDate: dateRange.start,
              endDate: dateRange.end,
              groupBy: 'user',
              userIds,
              boardIds:
                filters.boardIds.length > 0 ? filters.boardIds : undefined,
            },
            controller.signal,
          )
        : Promise.resolve(null);

      const [workloadResult, worklogsResp] = await Promise.all([
        workloadPromise,
        worklogsPromise,
      ]);

      if (controller.signal.aborted) return;

      // Build capacity totals from workload entries: userId → total available hours
      const capacityByUser = new Map<string, number>();
      // Build planned totals from workload entries: userId → total planned hours
      const plannedByUser = new Map<string, number>();

      for (const entry of workloadResult.entries) {
        capacityByUser.set(
          entry.userId,
          (capacityByUser.get(entry.userId) ?? 0) + entry.availableHours,
        );
        plannedByUser.set(
          entry.userId,
          (plannedByUser.get(entry.userId) ?? 0) + entry.scheduledHours,
        );
      }

      // Build logged totals: userId → total logged hours + billable hours
      const loggedByUser = new Map<string, number>();
      const billableByUser = new Map<string, number>();
      if (worklogsResp) {
        for (const row of worklogsResp.rows) {
          loggedByUser.set(
            row.groupKey,
            (loggedByUser.get(row.groupKey) ?? 0) + row.hours,
          );
          billableByUser.set(
            row.groupKey,
            (billableByUser.get(row.groupKey) ?? 0) + (row.billableHours ?? 0),
          );
        }
      }

      // Build user lookup
      const userMap = new Map(users.map((u) => [String(u.id), u]));

      // Build ProgressRows
      const progressRows: ProgressRow[] = userIds.map((uid) => {
        const user = userMap.get(uid);
        const firstTeam = user?.teams?.[0];
        return {
          userId: uid,
          userName: user?.name ?? `User #${uid}`,
          userPhoto: user?.photo_thumb_small ?? null,
          teamId: firstTeam ? String(firstTeam.id) : null,
          teamName: firstTeam?.name ?? null,
          requiredHours:
            Math.round((capacityByUser.get(uid) ?? 0) * 100) / 100,
          loggedHours: Math.round((loggedByUser.get(uid) ?? 0) * 100) / 100,
          plannedHours: Math.round((plannedByUser.get(uid) ?? 0) * 100) / 100,
          billableHours: needsLogged
            ? Math.round((billableByUser.get(uid) ?? 0) * 100) / 100
            : null,
        };
      });

      // Group rows
      const groups = groupProgressRows(progressRows, filters.teamIds);
      setProgressGroups(groups);
    } catch (err) {
      if (controller.signal.aborted) return;
      const message =
        err instanceof Error
          ? err.message
          : 'Failed to load progress data';
      setProgressError(message);
      console.error('Progress data fetch error:', err);
    } finally {
      if (!controller.signal.aborted) {
        setProgressLoading(false);
      }
    }
  }, [
    groupBy,
    dateRange,
    filters,
    users,
    setProgressGroups,
    setProgressLoading,
    setProgressError,
  ]);

  useEffect(() => {
    fetchData();
    return () => {
      abortRef.current?.abort();
    };
  }, [fetchData]);
}
